import MenyNativeEvent from "@app/events/MenyNativeEvent"
import { isJwt } from "@app/helpers"
import selectAppVersions from "@app/helpers/selectAppVersions"
import { hideWelcomePopup } from "@app/store/app/appActions"
import { RootState } from "@app/types"
import { qrLoading } from "@modules/user/store/actions/creators/qrCreator"
import { resetAuthedBaseData, setToken, updateToken } from "@modules/user/store/actions/user"
import * as core from "@ng-mw/framework-core"
import { App, LocalStorage, Logger, NativeEventBridge } from "@ng-mw/shared-react-components"
import { Store } from "redux"

const TOKEN_SHOULD_BE_VALID_TIME = 60000

export default (store: Store<RootState, any>) => {
    let lastTokenRefreshTs: number = 0
    let isRefreshing = false

    const performLogout = () => {
        store.dispatch(resetAuthedBaseData())
    }

    core.MessageBroker.registerListener(
        core.PredefinedMessageTopics.ACCESS_TOKEN_NEEDS_REFRESH,
        (data: unknown) => {
            const eventDetails = typeof data === "object" && data !== null ? data : {}
            isRefreshing = true
            LocalStorage.remove("DEBUG_STORED_TOKEN") // remove debug token when it's stale
            Logger.info("ACCESS_TOKEN_NEEDS_REFRESH, trying to refresh token", {
                eventDetails,
                ...selectAppVersions(store),
            })
            NativeEventBridge.broadcastNative(MenyNativeEvent.RefreshAccessToken, {
                forceRefresh: 0,
            })
        },
    )

    core.MessageBroker.registerListener(
        core.PredefinedMessageTopics.GOT_401_UNAUTHORIZED,
        (data: unknown) => {
            const now = new Date().getTime()
            const eventDetails = typeof data === "object" && data !== null ? data : {}
            if (isRefreshing) {
                Logger.info("GOT_401_UNAUTHORIZED, doing nothing: is already refreshing", {
                    eventDetails,
                    ...selectAppVersions(store),
                })
            } else if (lastTokenRefreshTs + TOKEN_SHOULD_BE_VALID_TIME > now) {
                Logger.info(
                    "GOT_401_UNAUTHORIZED, doing nothing: refreshed less than a minute ago",
                    {
                        eventDetails,
                        ...selectAppVersions(store),
                    },
                )
            } else {
                Logger.info("GOT_401_UNAUTHORIZED, trying to refresh token", {
                    eventDetails,
                    ...selectAppVersions(store),
                })
                isRefreshing = true
                LocalStorage.remove("DEBUG_STORED_TOKEN") // remove debug token when it's stale
                NativeEventBridge.broadcastNative(MenyNativeEvent.RefreshAccessToken, {
                    forceRefresh: 1,
                })
            }
        },
    )

    // core.MessageBroker.registerListener(
    //     core.PredefinedMessageTopics.TRUMF_USER_WILL_BE_LOGGED_OUT,
    //     (data: unknown) => {
    //         const eventDetails = typeof data === "object" && data !== null ? data : {}

    //         Logger.info("messagebroker.topic.trumfuserwillbeloggedout", {
    //             eventDetails,
    //             ...selectAppVersions(store),
    //         })

    //         NativeEventBridge.broadcastNative(MenyNativeEvent.ForceLogOut)
    //         performLogout()
    //     },
    // )

    core.MessageBroker.registerListener(core.PredefinedMessageTopics.LOGOUT_COMPLETE, () => {
        lastTokenRefreshTs = new Date().getTime()
        isRefreshing = false
        Logger.info("Got LOGOUT_COMPLETE resetting flags", {
            ...selectAppVersions(store),
        })
        performLogout()
    })

    NativeEventBridge.listen(
        MenyNativeEvent.LoginComplete,
        ({ token }: { token: string }) => {
            const legacyToken = LocalStorage.get(App.LocalStorage.UserToken)

            if (isJwt(token) && legacyToken) {
                LocalStorage.remove(App.LocalStorage.UserToken)
            }

            const state = store.getState()
            lastTokenRefreshTs = new Date().getTime()
            isRefreshing = false
            Logger.info("Got LoginComplete resetting flags", {
                token,
                ...selectAppVersions(store),
            })
            if (state.user.loginLevel > 0) {
                // This is a step-up - Just update token
                store.dispatch(updateToken(token))
            } else {
                // This is a new login - Set token from scratch
                store.dispatch(setToken(token))
            }

            store.dispatch(hideWelcomePopup())
            // TODO move the next lines to setToken() maybe?
            store.dispatch(qrLoading())
            NativeEventBridge.broadcastNative(MenyNativeEvent.GetTrumfQrCode)
        },
        false,
    )

    NativeEventBridge.listen(
        MenyNativeEvent.FreshAccessToken,
        ({ accessToken }: { accessToken: string }) => {
            lastTokenRefreshTs = new Date().getTime()
            isRefreshing = false
            Logger.info("Got freshAccessToken resetting flags", {
                // accessToken,
                ...selectAppVersions(store),
            })

            const state = store.getState()
            if (state.user.loginLevel > 0) {
                // This is a normal refresh - Just update token
                store.dispatch(updateToken(accessToken))
            } else {
                // We've gotten a fresh token after previously been "logged out",
                // or a failure to fetch a token at startup in less than 4 seconds
                // - Set token from scratch as if it's a fresh login
                // to re-fetch extended user data etc.
                store.dispatch(setToken(accessToken))
            }
        },
        false,
    )
}
